import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Plus,
  Edit2,
  Trash2,
  Users,
  Eye,
  EyeOff,
  Copy,
  ArrowLeft,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const API = process.env.REACT_APP_BACKEND_URL || "";

const StaffManagement = () => {
  const navigate = useNavigate();
  const [staff, setStaff] = useState([]);
  const [roles, setRoles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [showPIN, setShowPIN] = useState({});
  const [newPIN, setNewPIN] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    role_ids: [],
    restaurant_ids: [],
  });

  useEffect(() => {
    fetchStaff();
    fetchRoles();
  }, []);

  const getAuthHeader = () => {
    const token = localStorage.getItem("token");
    return { Authorization: `Bearer ${token}` };
  };

  const getCurrentRestaurantId = () => {
    const token = localStorage.getItem("token");
    if (!token) return null;

    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      return payload.restaurant_id;
    } catch (error) {
      return null;
    }
  };

  const fetchStaff = async () => {
    try {
      const response = await axios.get(`${API}/api/rbac/staff`, {
        headers: getAuthHeader(),
      });
      setStaff(response.data || []);
    } catch {
      toast.error("Failed to load staff");
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await axios.get(`${API}/api/rbac/roles`, {
        headers: getAuthHeader(),
      });
      setRoles(response.data || []);
    } catch {
      toast.error("Failed to load roles");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // 🔴 NAME
    if (!formData.name?.trim()) {
      toast.error("Full name is required");
      return;
    }

    // 🔴 EMAIL
    if (!formData.email?.trim()) {
      toast.error("Email is required");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email.trim())) {
      toast.error("Please enter a valid email");
      return;
    }

    // 🔴 DUPLICATE EMAIL (ignore current staff while editing)
    const emailExists = staff.some(
      (s) =>
        s.email === formData.email &&
        (!editingStaff || s.id !== editingStaff.id)
    );
    if (emailExists) {
      toast.error("Email already exists");
      return;
    }

    // 🔴 ROLE REQUIRED
    if (!formData.role_ids?.length) {
      toast.error("Please assign at least one role");
      return;
    }

    // 🔴 RESTAURANT CONTEXT
    const restaurantId = getCurrentRestaurantId();
    if (!restaurantId) {
      toast.error("Restaurant context missing. Please re-login.");
      return;
    }

    try {
      const restaurantIds = formData.restaurant_ids.includes(restaurantId)
        ? formData.restaurant_ids
        : [...formData.restaurant_ids, restaurantId];

      const submitData = {
        ...formData,
        restaurant_ids: restaurantIds,
      };

      if (editingStaff) {
        await axios.put(
          `${API}/api/rbac/staff/${editingStaff.id}`,
          submitData,
          { headers: getAuthHeader() }
        );
        toast.success("Staff updated successfully");
        setNewPIN(null);
        closeModal();
      } else {
        const response = await axios.post(`${API}/api/rbac/staff`, submitData, {
          headers: getAuthHeader(),
        });
        setNewPIN(response.data.pin);
        toast.success("Staff created successfully");
      }

      fetchStaff();
    } catch (error) {
      console.error("Error saving staff:", error);
      toast.error(error.response?.data?.detail || "Failed to save staff");
    }
  };

  const handleDelete = async (staffId, staffName) => {
    if (!window.confirm(`Are you sure you want to delete "${staffName}"?`))
      return;

    try {
      await axios.delete(`${API}/api/rbac/staff/${staffId}`, {
        headers: getAuthHeader(),
      });
      toast.success("Staff deleted successfully");
      fetchStaff();
    } catch {
      toast.error("Failed to delete staff");
    }
  };

  const openCreateModal = () => {
    setEditingStaff(null);
    setNewPIN(null);
    setFormData({
      name: "",
      email: "",
      phone: "",
      role_ids: [],
      restaurant_ids: [],
    });
    setShowModal(true);
  };

  const openEditModal = (staffMember) => {
    setEditingStaff(staffMember);
    setNewPIN(null);
    setFormData({
      name: staffMember.name,
      email: staffMember.email,
      phone: staffMember.phone || "",
      role_ids: staffMember.role_ids || [],
      restaurant_ids: staffMember.restaurant_ids || [],
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingStaff(null);
    setNewPIN(null);
    setFormData({
      name: "",
      email: "",
      phone: "",
      role_ids: [],
      restaurant_ids: [],
    });
  };

  const togglePINVisibility = (staffId) => {
    setShowPIN((prev) => ({
      ...prev,
      [staffId]: !prev[staffId],
    }));
  };

  const copyPINToClipboard = (pin) => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard
        .writeText(pin)
        .then(() => {
          toast.success("PIN copied to clipboard");
        })
        .catch(() => {
          // Fallback for clipboard API failure
          copyFallback(pin);
        });
    } else {
      copyFallback(pin);
    }

    function copyFallback(text) {
      const textArea = document.createElement("textarea");
      textArea.value = text;
      textArea.style.position = "fixed";
      textArea.style.left = "-9999px";
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand("copy");
        toast.success("PIN copied to clipboard");
      } catch (err) {
        toast.error("Failed to copy PIN");
      }
      document.body.removeChild(textArea);
    }
  };

  const getRoleNames = (roleIds) => {
    return roleIds
      .map((id) => roles.find((r) => r.id === id)?.name)
      .filter(Boolean)
      .join(", ");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-lg">Loading staff...</div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate(-1)}
          className="mb-4 flex items-center gap-2 text-gray-600 hover:text-gray-900 transition"
        >
          <ArrowLeft className="w-5 h-5" />
          Back
        </button>

        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
              <Users className="w-8 h-8 text-blue-600" />
              Staff Management
            </h1>
            <p className="text-gray-600 mt-1">
              Manage staff members and their access
            </p>
          </div>
          <button
            onClick={openCreateModal}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition"
          >
            <Plus className="w-5 h-5" />
            Add Staff
          </button>
        </div>
      </div>

      {/* Staff Table */}
      <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Phone
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Roles
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  PIN
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {staff.map((member) => (
                <tr key={member.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="font-medium text-gray-900">
                      {member.name}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                    {member.email}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                    {member.phone || "-"}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-wrap gap-1">
                      {(member.role_ids || []).map((roleId, idx) => {
                        const role = roles.find((r) => r.id === roleId);
                        return role ? (
                          <span
                            key={idx}
                            className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded"
                          >
                            {role.name}
                          </span>
                        ) : null;
                      })}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-sm">
                        {showPIN[member.id] ? member.pin : "••••"}
                      </span>
                      <button
                        onClick={() => togglePINVisibility(member.id)}
                        className="text-gray-500 hover:text-gray-700"
                      >
                        {showPIN[member.id] ? (
                          <EyeOff className="w-4 h-4" />
                        ) : (
                          <Eye className="w-4 h-4" />
                        )}
                      </button>
                      <button
                        onClick={() => copyPINToClipboard(member.pin)}
                        className="text-gray-500 hover:text-gray-700"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`px-2 py-1 text-xs rounded-full ${
                        member.is_active
                          ? "bg-green-100 text-green-800"
                          : "bg-red-100 text-red-800"
                      }`}
                    >
                      {member.is_active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex gap-2">
                      <button
                        onClick={() => openEditModal(member)}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(member.id, member.name)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {staff.length === 0 && (
        <div className="text-center py-12 text-gray-500 bg-white rounded-lg mt-4">
          <Users className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <p className="text-lg">
            No staff members found. Add your first staff member to get started.
          </p>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-900">
                {editingStaff ? "Edit Staff Member" : "Add New Staff Member"}
              </h2>
              <button
                onClick={closeModal}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>

            {/* Show PIN after creation */}
            {newPIN && (
              <div className="mx-6 mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm font-semibold text-green-800 mb-2">
                  Staff created successfully! Here is the PIN:
                </p>
                <div className="flex items-center justify-between bg-white p-3 rounded border border-green-300">
                  <span className="text-2xl font-mono font-bold text-gray-900">
                    {newPIN}
                  </span>
                  <button
                    onClick={() => copyPINToClipboard(newPIN)}
                    className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded flex items-center gap-2"
                  >
                    <Copy className="w-4 h-4" />
                    Copy
                  </button>
                </div>
                <p className="text-xs text-green-700 mt-2">
                  ⚠️ Save this PIN! It will be used for staff login and cannot
                  be recovered.
                </p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="John Doe"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) =>
                      setFormData({ ...formData, email: e.target.value })
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="john@example.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Phone
                  </label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) =>
                      setFormData({ ...formData, phone: e.target.value })
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="+1234567890"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Assign Roles *
                  </label>
                  <div className="space-y-2 max-h-48 overflow-y-auto border border-gray-300 rounded-lg p-3">
                    {roles.map((role) => (
                      <label
                        key={role.id}
                        className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-2 rounded"
                      >
                        <input
                          type="checkbox"
                          checked={formData.role_ids.includes(role.id)}
                          onChange={(e) => {
                            setFormData((prev) => ({
                              ...prev,
                              role_ids: e.target.checked
                                ? [...prev.role_ids, role.id]
                                : prev.role_ids.filter((id) => id !== role.id),
                            }));
                          }}
                          className="w-4 h-4"
                        />
                        <div>
                          <span className="text-sm font-medium text-gray-900">
                            {role.name}
                          </span>
                          <span className="text-xs text-gray-500 ml-2">
                            ({role.permissions?.length || 0} permissions)
                          </span>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3 justify-end mt-6">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition"
                >
                  {newPIN ? "Close" : "Cancel"}
                </button>
                {!newPIN && (
                  <button
                    type="submit"
                    className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition"
                  >
                    {editingStaff ? "Update Staff" : "Create Staff"}
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default StaffManagement;
